/**
 * Move-in / Move-out — RWA tenancy transition workflow.
 *
 * One PageHeader, KPI strip, kind + status filter pills, single transitions table.
 * "Start transition" dialog captures kind + unit + outgoing resident (if any) +
 * incoming name/phone (if any) + notice/effective dates.
 *
 * Row actions (kebab):
 *   - Advance status (notice → handover → key → noc_issued)
 *   - Issue NOC (jumps to /nocs with prefilled transition)
 *   - Complete (move-out finalises soft-delete; move-in creates Resident)
 *   - Cancel (with reason)
 *   - View — drawer with timeline + linked NOCs
 */
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeftRight,
  Plus,
  MoreHorizontal,
  ArrowDownToLine,
  ArrowUpFromLine,
  Replace,
  CalendarClock,
  FileSignature
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDate, formatDateTime } from '@/lib/format';

// ─── types ───

type TransitionKind = 'move_in' | 'move_out' | 'tenant_swap';
type TransitionStatus =
  | 'draft'
  | 'notice_given'
  | 'handover_scheduled'
  | 'key_transferred'
  | 'noc_issued'
  | 'completed'
  | 'cancelled';

type NocPurpose =
  | 'move_out'
  | 'vehicle_registration'
  | 'passport'
  | 'electricity_connection'
  | 'gas_connection'
  | 'internet_connection'
  | 'general'
  | 'other';

interface UnitLite {
  id: string;
  number: string;
  block: { id: string; name: string } | null;
}

interface NocLite {
  id: string;
  noc_number: string;
  purpose: NocPurpose;
  issued_at: string;
  revoked_at: string | null;
}

interface TenancyTransition {
  id: string;
  society_id: string;
  unit_id: string;
  kind: TransitionKind;
  status: TransitionStatus;
  outgoing_resident_id: string | null;
  incoming_resident_id: string | null;
  outgoing_full_name: string | null;
  incoming_full_name: string | null;
  notice_date: string | null;
  effective_date: string | null;
  handover_date: string | null;
  key_transferred_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  notes: string | null;
  pending_dues_inr: number;
  lease_end_date: string | null;
  monthly_rent_inr: number | null;
  deposit_inr: number | null;
  incoming_email: string | null;
  created_at: string;
  updated_at: string;
  unit: UnitLite;
  nocs: NocLite[];
}

interface ListResponse {
  items: TenancyTransition[];
  next_cursor: string | null;
}

interface UnitListResponse {
  items: UnitLite[];
}

interface ResidentLite {
  id: string;
  full_name: string;
  phone: string;
  unit_id: string;
  unit: UnitLite | null;
}

interface ResidentListResponse {
  items: ResidentLite[];
  next_cursor: string | null;
}

// ─── labels ───

const KIND_LABEL: Record<TransitionKind, string> = {
  move_in: 'Move in',
  move_out: 'Move out',
  tenant_swap: 'Tenant swap'
};

const STATUS_LABEL: Record<TransitionStatus, string> = {
  draft: 'Draft',
  notice_given: 'Notice given',
  handover_scheduled: 'Handover scheduled',
  key_transferred: 'Key transferred',
  noc_issued: 'NOC issued',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

const STATUS_VARIANT: Record<TransitionStatus, 'muted' | 'info' | 'warn' | 'ok' | 'alert'> = {
  draft: 'muted',
  notice_given: 'info',
  handover_scheduled: 'info',
  key_transferred: 'warn',
  noc_issued: 'warn',
  completed: 'ok',
  cancelled: 'alert'
};

const NEXT_STATUS: Partial<Record<TransitionStatus, TransitionStatus>> = {
  draft: 'notice_given',
  notice_given: 'handover_scheduled',
  handover_scheduled: 'key_transferred',
  key_transferred: 'noc_issued'
};

const STATUS_OPTIONS: TransitionStatus[] = [
  'draft',
  'notice_given',
  'handover_scheduled',
  'key_transferred',
  'noc_issued',
  'completed',
  'cancelled'
];

// ─── form schema ───

const CreateSchema = z
  .object({
    kind: z.enum(['move_in', 'move_out', 'tenant_swap']),
    unit_id: z.string().uuid({ message: 'Pick a unit' }),
    outgoing_resident_id: z.string().uuid().optional().or(z.literal('')),
    incoming_full_name: z.string().trim().max(120).optional().or(z.literal('')),
    incoming_phone: z.string().trim().max(20).optional().or(z.literal('')),
    incoming_email: z.string().trim().email().max(160).optional().or(z.literal('')),
    notice_date: z.string().optional().or(z.literal('')),
    effective_date: z.string().optional().or(z.literal('')),
    // Combined-wizard adds (all optional)
    lease_end_date: z.string().optional().or(z.literal('')),
    monthly_rent_inr: z.coerce.number().int().min(0).max(10_000_000).optional().or(z.literal('')),
    deposit_inr: z.coerce.number().int().min(0).max(10_000_000).optional().or(z.literal('')),
    issue_noc_inline: z.boolean().default(false),
    notes: z.string().trim().max(1000).optional().or(z.literal(''))
  })
  .superRefine((val, ctx) => {
    if (val.kind === 'move_out' && !val.outgoing_resident_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['outgoing_resident_id'], message: 'Outgoing resident required' });
    }
    if ((val.kind === 'move_in' || val.kind === 'tenant_swap') && !val.incoming_full_name) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['incoming_full_name'], message: 'Incoming name required' });
    }
    if (val.kind === 'tenant_swap' && !val.outgoing_resident_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['outgoing_resident_id'], message: 'Outgoing resident required' });
    }
  });
type CreateForm = z.infer<typeof CreateSchema>;

const CancelSchema = z.object({
  reason: z.string().trim().min(2, 'Reason required').max(300)
});
type CancelForm = z.infer<typeof CancelSchema>;

const CompleteSchema = z.object({
  incoming_full_name: z.string().trim().max(120).optional().or(z.literal('')),
  incoming_phone: z.string().trim().max(20).optional().or(z.literal(''))
});
type CompleteForm = z.infer<typeof CompleteSchema>;

// ─── helpers ───

function formatInr(n: number): string {
  return `₹${n.toLocaleString('en-IN')}`;
}

function unitLabel(u: UnitLite | null | undefined): string {
  if (!u) return '—';
  return u.block ? `${u.block.name} / ${u.number}` : u.number;
}

function fromToLabel(t: TenancyTransition): string {
  const out = t.outgoing_full_name ?? (t.kind === 'move_in' ? '—' : '?');
  const inc = t.incoming_full_name ?? (t.kind === 'move_out' ? '—' : '?');
  return `${out} → ${inc}`;
}

function toIsoDateTime(yyyyMmDd: string): string | undefined {
  if (!yyyyMmDd) return undefined;
  return new Date(`${yyyyMmDd}T00:00:00.000Z`).toISOString();
}

// ─── component ───

export function MoveInOutPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [kindFilter, setKindFilter] = useState<'all' | TransitionKind>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | TransitionStatus>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<TenancyTransition | null>(null);
  const [completeTarget, setCompleteTarget] = useState<TenancyTransition | null>(null);
  const [viewTarget, setViewTarget] = useState<TenancyTransition | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  // Data queries — cursor paginated.
  const transitionsFiltersHash = useMemo(
    () => JSON.stringify([kindFilter, statusFilter]),
    [kindFilter, statusFilter]
  );
  const [transitionsCursor, setTransitionsCursor] = useState<string | null>(null);
  useEffect(() => {
    setTransitionsCursor(null);
  }, [transitionsFiltersHash]);
  const transitionsQuery = useQuery({
    queryKey: ['tenancy-transitions', kindFilter, statusFilter, transitionsCursor],
    queryFn: () =>
      get<ListResponse>('/tenancy-transitions', {
        params: {
          limit: 50,
          ...(kindFilter !== 'all' ? { kind: kindFilter } : {}),
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          ...(transitionsCursor ? { cursor: transitionsCursor } : {})
        }
      })
  });
  const transitionsPaginator = useCursorAccumulator<TenancyTransition>(
    transitionsQuery.data,
    transitionsCursor,
    transitionsFiltersHash
  );
  const transitions = transitionsPaginator.items;

  const unitsQuery = useQuery({
    queryKey: ['tenancy-units'],
    queryFn: () => get<UnitListResponse>('/society/units', { params: { limit: 500 } }),
    staleTime: 60_000
  });
  const units = unitsQuery.data?.items ?? [];

  const residentsQuery = useQuery({
    queryKey: ['tenancy-residents'],
    queryFn: () => get<ResidentListResponse>('/residents', { params: { limit: 500 } }),
    staleTime: 60_000
  });
  const residents = residentsQuery.data?.items ?? [];

  // KPI strip
  const kpiActive = transitions.filter(
    (t) => t.status !== 'completed' && t.status !== 'cancelled'
  ).length;
  const kpiThisMonth = useMemo(() => {
    const monthStart = new Date();
    monthStart.setUTCDate(1);
    monthStart.setUTCHours(0, 0, 0, 0);
    return transitions.filter((t) => new Date(t.created_at).getTime() >= monthStart.getTime()).length;
  }, [transitions]);
  const kpiPendingNocs = transitions.filter(
    (t) => (t.kind === 'move_out' || t.kind === 'tenant_swap') &&
      t.status !== 'completed' &&
      t.status !== 'cancelled' &&
      t.nocs.filter((n) => n.revoked_at === null).length === 0
  ).length;

  // Create form
  const createForm = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: {
      kind: 'move_out',
      unit_id: '',
      outgoing_resident_id: '',
      incoming_full_name: '',
      incoming_phone: '',
      incoming_email: '',
      notice_date: '',
      effective_date: '',
      lease_end_date: '',
      monthly_rent_inr: '',
      deposit_inr: '',
      issue_noc_inline: false,
      notes: ''
    },
    mode: 'onChange'
  });
  const watchKind = createForm.watch('kind');
  const watchUnitId = createForm.watch('unit_id');
  const eligibleResidents = useMemo(
    () => residents.filter((r) => r.unit_id === watchUnitId),
    [residents, watchUnitId]
  );

  const create = useMutation({
    mutationFn: (v: CreateForm) =>
      post<TenancyTransition>('/tenancy-transitions', {
        kind: v.kind,
        unit_id: v.unit_id,
        ...(v.outgoing_resident_id ? { outgoing_resident_id: v.outgoing_resident_id } : {}),
        ...(v.incoming_full_name ? { incoming_full_name: v.incoming_full_name } : {}),
        ...(v.incoming_phone ? { incoming_phone: v.incoming_phone } : {}),
        ...(v.incoming_email ? { incoming_email: v.incoming_email } : {}),
        ...(v.notice_date ? { notice_date: toIsoDateTime(v.notice_date) } : {}),
        ...(v.effective_date ? { effective_date: toIsoDateTime(v.effective_date) } : {}),
        ...(v.lease_end_date ? { lease_end_date: v.lease_end_date } : {}),
        ...(typeof v.monthly_rent_inr === 'number' && v.monthly_rent_inr >= 0
          ? { monthly_rent_inr: v.monthly_rent_inr }
          : {}),
        ...(typeof v.deposit_inr === 'number' && v.deposit_inr >= 0
          ? { deposit_inr: v.deposit_inr }
          : {}),
        ...(v.notes ? { notes: v.notes } : {})
      }),
    onSuccess: (created) => {
      toast.success('Transition started');
      const issueNocInline = createForm.getValues('issue_noc_inline');
      setCreateOpen(false);
      createForm.reset({
        kind: 'move_out',
        unit_id: '',
        outgoing_resident_id: '',
        incoming_full_name: '',
        incoming_phone: '',
        incoming_email: '',
        notice_date: '',
        effective_date: '',
        lease_end_date: '',
        monthly_rent_inr: '',
        deposit_inr: '',
        issue_noc_inline: false,
        notes: ''
      });
      void queryClient.invalidateQueries({ queryKey: ['tenancy-transitions'] });
      if (issueNocInline) {
        // Jump straight to NOC issuance with this transition prefilled.
        void navigate({ to: '/nocs', search: { transition_id: created.id } as never });
      }
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const advance = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TransitionStatus }) =>
      patch<TenancyTransition>(`/tenancy-transitions/${id}`, { status }),
    onSuccess: (updated) => {
      toast.success(`Advanced to ${STATUS_LABEL[updated.status]}`);
      void queryClient.invalidateQueries({ queryKey: ['tenancy-transitions'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Cancel form
  const cancelForm = useForm<CancelForm>({ resolver: zodResolver(CancelSchema), defaultValues: { reason: '' } });
  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      post<TenancyTransition>(`/tenancy-transitions/${id}/cancel`, { reason }),
    onSuccess: () => {
      toast.success('Transition cancelled');
      setCancelTarget(null);
      cancelForm.reset({ reason: '' });
      void queryClient.invalidateQueries({ queryKey: ['tenancy-transitions'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Complete form
  const completeForm = useForm<CompleteForm>({
    resolver: zodResolver(CompleteSchema),
    defaultValues: { incoming_full_name: '', incoming_phone: '' }
  });
  const complete = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, string> }) =>
      post<TenancyTransition>(`/tenancy-transitions/${id}/complete`, body),
    onSuccess: () => {
      toast.success('Transition completed');
      setCompleteTarget(null);
      completeForm.reset({ incoming_full_name: '', incoming_phone: '' });
      void queryClient.invalidateQueries({ queryKey: ['tenancy-transitions'] });
      void queryClient.invalidateQueries({ queryKey: ['residents'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── handlers ───

  const onSubmitCreate = (v: CreateForm): void => {
    create.mutate(v);
  };

  const onSubmitCancel = (v: CancelForm): void => {
    if (!cancelTarget) return;
    cancel.mutate({ id: cancelTarget.id, reason: v.reason });
  };

  const onSubmitComplete = (v: CompleteForm): void => {
    if (!completeTarget) return;
    const body: Record<string, string> = {};
    if (v.incoming_full_name) body.incoming_full_name = v.incoming_full_name;
    if (v.incoming_phone) body.incoming_phone = v.incoming_phone;
    complete.mutate({ id: completeTarget.id, body });
  };

  const openComplete = (t: TenancyTransition): void => {
    setCompleteTarget(t);
    completeForm.reset({
      incoming_full_name: t.incoming_full_name ?? '',
      incoming_phone: ''
    });
  };

  const KIND_ICON: Record<TransitionKind, JSX.Element> = {
    move_in: <ArrowDownToLine className="h-4 w-4 text-vt-primary" aria-hidden="true" />,
    move_out: <ArrowUpFromLine className="h-4 w-4 text-vt-alert" aria-hidden="true" />,
    tenant_swap: <Replace className="h-4 w-4 text-vt-text-muted" aria-hidden="true" />
  };

  return (
    <>
      <PageHeader
        title="Move-in / Move-out"
        description="Tenancy transitions for owners and tenants — captures notice, handover, key transfer, NOC issuance and final completion."
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Start transition
          </Button>
        }
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-3">
          <KpiCard label="Active transitions" value={kpiActive} icon={<ArrowLeftRight className="h-5 w-5" />} />
          <KpiCard label="This month" value={kpiThisMonth} icon={<CalendarClock className="h-5 w-5" />} />
          <KpiCard label="Pending NOCs" value={kpiPendingNocs} icon={<FileSignature className="h-5 w-5" />} />
        </div>

        {/* Kind pills */}
        <div className="flex flex-wrap items-center gap-vt-2">
          <TabsPills
            size="sm"
            value={kindFilter}
            onChange={(v) => setKindFilter(v as 'all' | TransitionKind)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'move_in', label: 'Move in' },
              { value: 'move_out', label: 'Move out' },
              { value: 'tenant_swap', label: 'Tenant swap' }
            ]}
            aria-label="Transition kind filter"
          />

          <div className="ml-auto flex items-center gap-vt-2">
            <Label htmlFor="status-filter" className="text-vt-13 text-vt-text-muted">Status</Label>
            <Select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | TransitionStatus)}
            >
              <option value="all">All</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </Select>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {transitionsQuery.isLoading ? (
              <div className="space-y-vt-2 p-vt-4">
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : transitions.length === 0 ? (
              <EmptyState
                icon={<ArrowLeftRight className="h-6 w-6" />}
                title="No transitions yet"
                description="Start a transition when a resident hands in notice or a new tenant moves in."
              />
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kind</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Outgoing → Incoming</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notice</TableHead>
                    <TableHead>Effective</TableHead>
                    <TableHead className="text-right">Pending dues</TableHead>
                    <TableHead className="w-[44px]"> </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transitions.map((t) => {
                    const isTerminal = t.status === 'completed' || t.status === 'cancelled';
                    const nextStatus = NEXT_STATUS[t.status];
                    return (
                      <TableRow key={t.id}>
                        <TableCell>
                          <span className="inline-flex items-center gap-vt-2 text-vt-14">
                            {KIND_ICON[t.kind]}
                            {KIND_LABEL[t.kind]}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium text-vt-text">{unitLabel(t.unit)}</TableCell>
                        <TableCell className="text-vt-text-muted">{fromToLabel(t)}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[t.status]}>{STATUS_LABEL[t.status]}</Badge>
                        </TableCell>
                        <TableCell>{formatDate(t.notice_date)}</TableCell>
                        <TableCell>{formatDate(t.effective_date)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {t.pending_dues_inr > 0 ? (
                            <span className="text-vt-warn">{formatInr(t.pending_dues_inr)}</span>
                          ) : (
                            <span className="text-vt-text-muted">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="relative">
                            <button
                              type="button"
                              aria-label="Actions"
                              className="inline-flex h-7 w-7 items-center justify-center rounded-vt-sm text-vt-text-muted hover:bg-vt-surface-2 hover:text-vt-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
                              onClick={() => setMenuOpenId((id) => (id === t.id ? null : t.id))}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                            {menuOpenId === t.id ? (
                              <div
                                className="absolute right-0 top-8 z-20 w-52 overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface shadow-vt-2"
                                onMouseLeave={() => setMenuOpenId(null)}
                              >
                                <MenuItem
                                  onClick={() => {
                                    setMenuOpenId(null);
                                    setViewTarget(t);
                                  }}
                                >
                                  View timeline
                                </MenuItem>
                                {!isTerminal && nextStatus ? (
                                  <MenuItem
                                    onClick={() => {
                                      setMenuOpenId(null);
                                      advance.mutate({ id: t.id, status: nextStatus });
                                    }}
                                  >
                                    Advance to {STATUS_LABEL[nextStatus]}
                                  </MenuItem>
                                ) : null}
                                {!isTerminal ? (
                                  <Link
                                    to="/nocs"
                                    search={{ transition_id: t.id, unit_id: t.unit_id } as never}
                                    onClick={() => setMenuOpenId(null)}
                                    className="block w-full px-vt-3 py-vt-2 text-left text-vt-13 text-vt-text hover:bg-vt-surface-2"
                                  >
                                    Issue NOC
                                  </Link>
                                ) : null}
                                {!isTerminal && t.status !== 'draft' ? (
                                  <MenuItem
                                    onClick={() => {
                                      setMenuOpenId(null);
                                      openComplete(t);
                                    }}
                                  >
                                    Complete
                                  </MenuItem>
                                ) : null}
                                {!isTerminal ? (
                                  <MenuItem
                                    danger
                                    onClick={() => {
                                      setMenuOpenId(null);
                                      setCancelTarget(t);
                                      cancelForm.reset({ reason: '' });
                                    }}
                                  >
                                    Cancel
                                  </MenuItem>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <CursorPaginator
                hasMore={Boolean(transitionsPaginator.nextCursor)}
                loading={transitionsQuery.isFetching}
                itemsShown={transitions.length}
                pageSize={50}
                onLoadMore={() => {
                  if (transitionsPaginator.nextCursor) setTransitionsCursor(transitionsPaginator.nextCursor);
                }}
              />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => setCreateOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start tenancy transition</DialogTitle>
            <DialogDescription>
              Pending maintenance dues are auto-snapshotted at notice time and shown on the NOC.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createForm.handleSubmit(onSubmitCreate)} className="flex flex-col gap-vt-4">
            <div>
              <Label className="mb-vt-2 block">Kind</Label>
              <div className="grid grid-cols-3 gap-vt-2">
                {(['move_in', 'move_out', 'tenant_swap'] as TransitionKind[]).map((k) => {
                  const checked = watchKind === k;
                  return (
                    <label
                      key={k}
                      className={
                        'flex cursor-pointer items-center justify-center rounded-vt-sm border px-vt-3 py-vt-2 text-vt-13 transition-colors ' +
                        (checked
                          ? 'border-vt-primary bg-vt-primary-100 text-vt-primary font-semibold'
                          : 'border-vt-border text-vt-text-muted hover:bg-vt-surface-2')
                      }
                    >
                      <input
                        type="radio"
                        value={k}
                        {...createForm.register('kind')}
                        className="sr-only"
                      />
                      {KIND_LABEL[k]}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="unit_id">Unit</Label>
              <Select id="unit_id" {...createForm.register('unit_id')}>
                <option value="">Select unit…</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {unitLabel(u)}
                  </option>
                ))}
              </Select>
              {createForm.formState.errors.unit_id ? (
                <p className="text-vt-12 text-vt-alert">{createForm.formState.errors.unit_id.message}</p>
              ) : null}
            </div>

            {watchKind !== 'move_in' ? (
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="outgoing_resident_id">Outgoing resident</Label>
                <Select id="outgoing_resident_id" {...createForm.register('outgoing_resident_id')}>
                  <option value="">{watchUnitId ? 'Select resident…' : 'Pick a unit first'}</option>
                  {eligibleResidents.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.full_name} ({r.phone})
                    </option>
                  ))}
                </Select>
                {createForm.formState.errors.outgoing_resident_id ? (
                  <p className="text-vt-12 text-vt-alert">{createForm.formState.errors.outgoing_resident_id.message}</p>
                ) : null}
              </div>
            ) : null}

            {watchKind !== 'move_out' ? (
              <div className="grid grid-cols-2 gap-vt-3">
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="incoming_full_name">Incoming name</Label>
                  <Input id="incoming_full_name" placeholder="Full name" {...createForm.register('incoming_full_name')} />
                  {createForm.formState.errors.incoming_full_name ? (
                    <p className="text-vt-12 text-vt-alert">{createForm.formState.errors.incoming_full_name.message}</p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="incoming_phone">Incoming phone</Label>
                  <Input id="incoming_phone" placeholder="+919...." {...createForm.register('incoming_phone')} />
                </div>
                <div className="col-span-2 flex flex-col gap-vt-1">
                  <Label htmlFor="incoming_email">Incoming email (optional)</Label>
                  <Input
                    id="incoming_email"
                    type="email"
                    placeholder="tenant@example.com"
                    {...createForm.register('incoming_email')}
                  />
                  {createForm.formState.errors.incoming_email ? (
                    <p className="text-vt-12 text-vt-alert">
                      {createForm.formState.errors.incoming_email.message}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="notice_date">Notice date</Label>
                <Input id="notice_date" type="date" {...createForm.register('notice_date')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="effective_date">
                  {watchKind === 'move_out' ? 'Effective date' : 'Lease start (effective)'}
                </Label>
                <Input id="effective_date" type="date" {...createForm.register('effective_date')} />
              </div>
            </div>

            {watchKind !== 'move_out' ? (
              <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
                <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                  Lease &amp; finance (optional)
                </div>
                <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
                  <div className="flex flex-col gap-vt-1">
                    <Label htmlFor="lease_end_date">Lease end date</Label>
                    <Input
                      id="lease_end_date"
                      type="date"
                      {...createForm.register('lease_end_date')}
                    />
                  </div>
                  <div className="flex flex-col gap-vt-1">
                    <Label htmlFor="monthly_rent_inr">Monthly rent (₹)</Label>
                    <Input
                      id="monthly_rent_inr"
                      type="number"
                      min={0}
                      placeholder="25000"
                      {...createForm.register('monthly_rent_inr')}
                    />
                  </div>
                  <div className="flex flex-col gap-vt-1">
                    <Label htmlFor="deposit_inr">Security deposit (₹)</Label>
                    <Input
                      id="deposit_inr"
                      type="number"
                      min={0}
                      placeholder="50000"
                      {...createForm.register('deposit_inr')}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" rows={3} placeholder="Internal notes…" {...createForm.register('notes')} />
            </div>

            {watchKind === 'move_out' || watchKind === 'tenant_swap' ? (
              <label className="flex items-start gap-vt-2 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-vt-primary"
                  {...createForm.register('issue_noc_inline')}
                />
                <span className="flex-1 text-vt-13">
                  <span className="font-semibold text-vt-text">Issue NOC right after creating</span>
                  <span className="block text-vt-12 text-vt-text-muted">
                    Jumps straight to the NOC issuance page with this transition pre-filled.
                  </span>
                </span>
              </label>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Starting…' : 'Start transition'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelTarget !== null} onOpenChange={(o) => (o ? null : setCancelTarget(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel transition</DialogTitle>
            <DialogDescription>
              {cancelTarget ? (
                <>
                  {KIND_LABEL[cancelTarget.kind]} for unit{' '}
                  <strong>{unitLabel(cancelTarget.unit)}</strong>. This action is final.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={cancelForm.handleSubmit(onSubmitCancel)} className="flex flex-col gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="cancel_reason">Reason</Label>
              <Textarea id="cancel_reason" rows={3} {...cancelForm.register('reason')} />
              {cancelForm.formState.errors.reason ? (
                <p className="text-vt-12 text-vt-alert">{cancelForm.formState.errors.reason.message}</p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCancelTarget(null)}>
                Keep open
              </Button>
              <Button type="submit" variant="danger" disabled={cancel.isPending}>
                {cancel.isPending ? 'Cancelling…' : 'Cancel transition'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={completeTarget !== null} onOpenChange={(o) => (o ? null : setCompleteTarget(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete transition</DialogTitle>
            <DialogDescription>
              {completeTarget ? (
                <>
                  {KIND_LABEL[completeTarget.kind]} for unit{' '}
                  <strong>{unitLabel(completeTarget.unit)}</strong>.
                  {completeTarget.kind === 'move_out'
                    ? ' The outgoing resident will be soft-deleted.'
                    : ' A new resident record will be created.'}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={completeForm.handleSubmit(onSubmitComplete)} className="flex flex-col gap-vt-3">
            {completeTarget && completeTarget.kind !== 'move_out' ? (
              <>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="c_incoming_full_name">Incoming full name</Label>
                  <Input id="c_incoming_full_name" {...completeForm.register('incoming_full_name')} />
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="c_incoming_phone">Incoming phone</Label>
                  <Input id="c_incoming_phone" placeholder="+919...." {...completeForm.register('incoming_phone')} />
                </div>
              </>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCompleteTarget(null)}>
                Not yet
              </Button>
              <Button type="submit" disabled={complete.isPending}>
                {complete.isPending ? 'Completing…' : 'Complete'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View / timeline dialog */}
      <Dialog open={viewTarget !== null} onOpenChange={(o) => (o ? null : setViewTarget(null))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {viewTarget ? `${KIND_LABEL[viewTarget.kind]} · ${unitLabel(viewTarget.unit)}` : 'Transition'}
            </DialogTitle>
            <DialogDescription>
              {viewTarget ? <>Created {formatDateTime(viewTarget.created_at)}</> : null}
            </DialogDescription>
          </DialogHeader>
          {viewTarget ? (
            <div className="flex flex-col gap-vt-4">
              <div className="grid grid-cols-2 gap-vt-3 text-vt-13">
                <Field label="Status"><Badge variant={STATUS_VARIANT[viewTarget.status]}>{STATUS_LABEL[viewTarget.status]}</Badge></Field>
                <Field label="Pending dues">{viewTarget.pending_dues_inr > 0 ? formatInr(viewTarget.pending_dues_inr) : '—'}</Field>
                <Field label="Outgoing">{viewTarget.outgoing_full_name ?? '—'}</Field>
                <Field label="Incoming">{viewTarget.incoming_full_name ?? '—'}</Field>
                <Field label="Notice date">{formatDate(viewTarget.notice_date)}</Field>
                <Field label="Effective date">{formatDate(viewTarget.effective_date)}</Field>
                <Field label="Key transferred">{formatDateTime(viewTarget.key_transferred_at)}</Field>
                <Field label="Completed">{formatDateTime(viewTarget.completed_at)}</Field>
              </div>
              {viewTarget.notes ? (
                <div>
                  <Label className="mb-vt-1 block">Notes</Label>
                  <p className="rounded-vt-sm border border-vt-border bg-vt-surface-2 p-vt-3 text-vt-13 text-vt-text">
                    {viewTarget.notes}
                  </p>
                </div>
              ) : null}
              {viewTarget.cancel_reason ? (
                <div>
                  <Label className="mb-vt-1 block">Cancel reason</Label>
                  <p className="rounded-vt-sm border border-vt-alert/30 bg-vt-alert/10 p-vt-3 text-vt-13 text-vt-alert">
                    {viewTarget.cancel_reason}
                  </p>
                </div>
              ) : null}
              <div>
                <Label className="mb-vt-1 block">Linked NOCs</Label>
                {viewTarget.nocs.length === 0 ? (
                  <p className="text-vt-13 text-vt-text-muted">No NOCs issued yet.</p>
                ) : (
                  <ul className="space-y-vt-1">
                    {viewTarget.nocs.map((n) => (
                      <li key={n.id} className="flex items-center justify-between rounded-vt-sm border border-vt-border px-vt-3 py-vt-2 text-vt-13">
                        <span className="font-mono">{n.noc_number}</span>
                        <span className="text-vt-text-muted">{n.purpose.replace(/_/g, ' ')}</span>
                        <span className="text-vt-text-muted">{formatDate(n.issued_at)}</span>
                        {n.revoked_at ? <Badge variant="alert">Revoked</Badge> : <Badge variant="ok">Active</Badge>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── small components ───

function KpiCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }): JSX.Element {
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-vt-4">
        <div>
          <div className="text-vt-12 uppercase tracking-[0.06em] text-vt-text-muted">{label}</div>
          <div className="mt-vt-1 text-vt-24 font-semibold text-vt-text tabular-nums">{value}</div>
        </div>
        <div className="rounded-vt-sm bg-vt-primary-100 p-vt-2 text-vt-primary">{icon}</div>
      </CardContent>
    </Card>
  );
}

function MenuItem({
  onClick,
  danger,
  children
}: {
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'block w-full px-vt-3 py-vt-2 text-left text-vt-13 hover:bg-vt-surface-2 ' +
        (danger ? 'text-vt-alert' : 'text-vt-text')
      }
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="text-vt-12 uppercase tracking-[0.06em] text-vt-text-muted">{label}</div>
      <div className="mt-vt-1 text-vt-text">{children}</div>
    </div>
  );
}
